import { DateTime } from 'luxon'
import { useSelector } from 'react-redux'

import PageHeader from './PageHeader.js'
import PageSection from './PageSection.js'

import { AttributeTable, AttributeRow } from './CommonStylesTables'
import styled from 'styled-components'

const TopAllignedTH = styled.th`
  vertical-align: top;
`

function Presentation(props) {
  const contacts = useSelector((state) => state.contacts.contacts)
  const presentationId = props.presentation
  const presentationReports = useSelector(
    (state) => state.presentations.presentationReports
  )

  let presentationSelected = ''
  let presWithVals = null

  presentationSelected =
    presentationReports.find(
      (pres) => pres.presentation_exchange_id === presentationId
    ) || ''

  // (anwalker293) presWithVals init

  let requestedSchema =
    presentationSelected?.presentation?.requested_proof?.revealed_attr_groups ||
    null
  let requestedSchemaSelfAttested =
    presentationSelected?.presentation?.requested_proof?.self_attested_attrs ||
    null

  // (anwalker293) We should only display attrs if the state is
  // in done or another equivalent ACA-Py state
  const desiredFinishedStates = ['done', 'verified']

  if (desiredFinishedStates.includes(presentationSelected?.state)) {
    // (anwalker293) If we have values, populate them
    // Else, just grab the keys
    if (
      presentationSelected?.presentation &&
      requestedSchema &&
      Object.keys(requestedSchema).length > 0
    ) {
      // (anwalker293) Regular
      const revealedAttrGroups =
        presentationSelected?.presentation?.requested_proof
          ?.revealed_attr_groups
      requestedSchema = Object.values(revealedAttrGroups ?? {})[0]?.values
      presWithVals = Object.fromEntries(
        Object.entries(
          Object.values(
            presentationSelected?.presentation?.requested_proof
              ?.revealed_attr_groups ?? {}
          )[0]?.values ?? {}
        ).map(([k, v]) => [k, v.raw])
      )
    } else if (
      presentationSelected?.presentation &&
      requestedSchemaSelfAttested &&
      Object.keys(requestedSchemaSelfAttested).length > 0
    ) {
      // (anwalker293) Self-attested
      const revealedAttrGroups =
        presentationSelected?.presentation?.requested_proof?.self_attested_attrs
      requestedSchema = revealedAttrGroups
      presWithVals = revealedAttrGroups
    } else if (
      presentationSelected?.presentation &&
      presentationSelected?.presentation['@context']
    ) {
      // (anwalker293) JSON-LD credential
      const tempRevealedAttrGroups =
        presentationSelected?.presentation.verifiableCredential[0]
      const revealedAttrGroups = {}
      const typeOfCredential = tempRevealedAttrGroups.type.find(
        (t) => t !== 'VerifiableCredential'
      )
      revealedAttrGroups['type'] = typeOfCredential
      revealedAttrGroups['id'] = tempRevealedAttrGroups.credentialSubject.id
      revealedAttrGroups[typeOfCredential] = {}

      // (anwalker293) Styling
      for (let key in tempRevealedAttrGroups.credentialSubject.achievement) {
        if (key === 'type') {
          revealedAttrGroups[typeOfCredential][key] =
            tempRevealedAttrGroups.credentialSubject.achievement[key][0]
        } else {
          revealedAttrGroups[typeOfCredential][key] =
            tempRevealedAttrGroups.credentialSubject.achievement[key]
        }
      }

      requestedSchema = revealedAttrGroups
      presWithVals = revealedAttrGroups
    } else {
      // (anwalker293) Extra handling
      requestedSchema =
        Object.values(
          presentationSelected?.presentation_request?.requested_attributes ??
            null
        )[0].names ?? null
    }
  } else {
    // (anwalker293) Display no attributes to display by default
    requestedSchema = null
  }

  let showPresentation = {}
  // Attributes of this kind of credential
  let displayAttributes = ''

  // (AmmonBurgi) Find connection and give the selected presentation a contact_label.
  for (let i = 0; i < contacts.length; i++) {
    if (
      contacts[i].Connections[0].connection_id ===
      presentationSelected.connection_id
    ) {
      presentationSelected.contact_label = contacts[i].label
      break
    }
  }

  if (presentationSelected !== '') {
    let displayAttributes = ''
    let attributes = {}

    const requestName = presentationSelected.presentation_request
      ? presentationSelected.presentation_request.name
      : ''

    let presentationRequestedProof = {}

    // (anwalker293) Attributes object determines PII
    if (presentationSelected && requestedSchema && presWithVals) {
      displayAttributes = renderAttributes(presWithVals)
    } else if (
      (presentationSelected && requestedSchema && !presWithVals) ||
      !requestedSchema
    ) {
      displayAttributes = (
        <tr>
          <td>No attributes to display</td>
        </tr>
      )
    }

    //(RomanStepanyan) recursively check all nested objects inside the credential to render images as well as text
    function renderAttributes(attr) {
      return Object.entries(attr).map(([key, value]) => {
        if (typeof value === 'object' && value !== null) {
          const nestedAttributes = renderAttributes(value)
          return (
            <AttributeRow key={key}>
              <TopAllignedTH>
                {key
                  .replaceAll('_', ' ')
                  .replace(/^\w/, (c) => c.toUpperCase())}
                :{' '}
              </TopAllignedTH>
              <td>
                <table>
                  <tbody>{nestedAttributes}</tbody>
                </table>
              </td>
            </AttributeRow>
          )
        } else {
          return (
            <AttributeRow key={key}>
              <TopAllignedTH>
                {key
                  .replaceAll('_', ' ')
                  .replace(/^\w/, (c) => c.toUpperCase())}
                :{' '}
              </TopAllignedTH>
              <td>
                {value?.startsWith('data:image/') ? (
                  <img src={value} alt={key.name} style={{ width: '200px' }} />
                ) : (
                  value
                )}
              </td>
            </AttributeRow>
          )
        }
      })
    }

    return (
      <div id="contact">
        <PageHeader
          title={
            'Presentation request from ' + presentationSelected.contact_label ||
            ''
          }
        />
        <PageSection>
          <h2>Presentation Information</h2>
          <AttributeTable>
            <tbody>
              <AttributeRow>
                <th>Contact Label:</th>
                <td>{presentationSelected.contact_label || ''}</td>
              </AttributeRow>
              <AttributeRow>
                <th>Connection ID:</th>
                <td>{presentationSelected.connection_id || ''}</td>
              </AttributeRow>
              <AttributeRow>
                <th>Initiator:</th>
                <td>{presentationSelected.initiator || ''}</td>
              </AttributeRow>
              <AttributeRow>
                <th>Presentation Exchange ID:</th>
                <td>{presentationSelected.presentation_exchange_id || ''}</td>
              </AttributeRow>
              <AttributeRow>
                <th>Request Name:</th>
                <td>{requestName || ''}</td>
              </AttributeRow>
              <AttributeRow>
                <th>Role:</th>
                <td>{presentationSelected.role || ''}</td>
              </AttributeRow>
              <AttributeRow>
                <th>Verified:</th>
                <td>{String(presentationSelected.verified) || ''}</td>
              </AttributeRow>
              <AttributeRow>
                <th>State:</th>
                <td>{presentationSelected.state.replaceAll('_', ' ') || ''}</td>
              </AttributeRow>
              <AttributeRow>
                <th>Thread ID:</th>
                <td>{presentationSelected.thread_id || ''}</td>
              </AttributeRow>
              <AttributeRow>
                <th>Date Updated:</th>
                <td>
                  {DateTime.fromISO(
                    presentationSelected.presentation_updated_at
                  ).toLocaleString(DateTime.DATETIME_MED) || ''}
                  {presentationSelected.presentation_updated_at}
                </td>
              </AttributeRow>
              <AttributeRow>
                <th>Date Created:</th>
                <td>
                  {DateTime.fromISO(
                    presentationSelected.presentation_created_at
                  ).toLocaleString(DateTime.DATETIME_MED) || ''}
                  {presentationSelected.presentation_created_at}
                </td>
              </AttributeRow>
            </tbody>
          </AttributeTable>
          <h2>Attributes</h2>
          <AttributeTable>
            <tbody>{displayAttributes}</tbody>
          </AttributeTable>
        </PageSection>
      </div>
    )
  } else {
    return (
      <div id="contact">
        <PageHeader title={'Presentation request'} />
        <PageSection>
          <h2>Presentation Information</h2>
        </PageSection>
      </div>
    )
  }
}

export default Presentation
